import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { CREATE_FLAG_TEMPLATE, UPDATE_FLAG_TEMPLATE } from '../../graphql/flagTemplates';
import { FLAG_ICONS, FlagIcon, getIconNames } from '../../utils/flag-icons';
import { calculateTextColor } from '../../utils/custom-flag-utils';
import { SketchPicker } from 'react-color';

const FLAG_COLORS = [
  { name: 'Red', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Gray', value: '#6b7280' }
];

function FlagTemplateEditor({ template, onClose, onSave }) {
  const [formData, setFormData] = useState({
    flagValue: '',
    iconName: 'tag',
    backgroundColor: '#3b82f6',
    textColor: null
  });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);

  const [createTemplate, { loading: creating }] = useMutation(CREATE_FLAG_TEMPLATE);
  const [updateTemplate, { loading: updating }] = useMutation(UPDATE_FLAG_TEMPLATE);

  const isLoading = creating || updating;
  const isEditing = !!template;

  useEffect(() => {
    if (template) {
      setFormData({
        flagValue: template.flagValue || '',
        iconName: template.iconName || 'tag',
        backgroundColor: template.backgroundColor || '#3b82f6',
        textColor: template.textColor || null
      });
    }
  }, [template]);

  // Auto-calculate text color when background changes
  useEffect(() => {
    if (!formData.textColor || !isEditing) {
      const calculatedColor = calculateTextColor(formData.backgroundColor);
      setFormData(prev => ({ ...prev, textColor: calculatedColor }));
    }
  }, [formData.backgroundColor, isEditing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleColorChange = (color) => {
    setFormData(prev => ({
      ...prev,
      backgroundColor: color.hex,
      textColor: calculateTextColor(color.hex)
    }));
  };

  const handleIconSelect = (iconName) => {
    setFormData(prev => ({ ...prev, iconName }));
    setShowIconPicker(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.flagValue.trim()) {
      toast.error('Flag text is required');
      return;
    }

    if (!formData.iconName) {
      toast.error('Please select an icon');
      return;
    }

    try {
      const input = {
        flagValue: formData.flagValue.trim(),
        iconName: formData.iconName,
        backgroundColor: formData.backgroundColor,
        textColor: formData.textColor,
        sortOrder: 0
      };

      if (isEditing) {
        await updateTemplate({
          variables: {
            input: {
              id: template.id,
              ...input
            }
          }
        });
        toast.success('Flag template updated successfully');
      } else {
        await createTemplate({
          variables: { input }
        });
        toast.success('Flag template created successfully');
      }

      onSave();
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to save flag template');
    }
  };

  const iconNames = getIconNames();

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditing ? 'Edit Flag Template' : 'Create Flag Template'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4">
          {/* Flag Text */}
          <div className="mb-4">
            <label htmlFor="flagValue" className="block text-sm font-medium text-gray-700 mb-1">
              Flag Text <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="flagValue"
              name="flagValue"
              value={formData.flagValue}
              onChange={handleChange}
              maxLength={50}
              required
              className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., VIP Guest, Urgent, Special Request"
            />
            <p className="mt-1 text-xs text-gray-500">{formData.flagValue.length}/50 characters</p>
          </div>

          {/* Icon Selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Icon <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowIconPicker(!showIconPicker)}
                className="w-full flex items-center justify-between border border-gray-300 rounded-md px-3 py-2 bg-white hover:bg-gray-50"
              >
                <div className="flex items-center">
                  <FlagIcon iconName={formData.iconName} className="h-5 w-5 mr-2" />
                  <span className="text-sm text-gray-700 capitalize">{formData.iconName}</span>
                </div>
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showIconPicker && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto">
                  <div className="grid grid-cols-4 gap-2 p-3">
                    {iconNames.map(iconName => (
                      <button
                        key={iconName}
                        type="button"
                        onClick={() => handleIconSelect(iconName)}
                        className={`p-3 rounded-md border-2 hover:border-blue-500 transition-colors ${
                          formData.iconName === iconName ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                        }`}
                        title={iconName}
                      >
                        <FlagIcon iconName={iconName} className="h-6 w-6 mx-auto" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Background Color */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Background Color <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center space-x-2">
              <div className="flex-1">
                <div className="flex flex-wrap gap-2">
                  {FLAG_COLORS.map(color => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => handleColorChange({ hex: color.value })}
                      className={`w-10 h-10 rounded-md border-2 ${
                        formData.backgroundColor === color.value
                          ? 'border-gray-900 ring-2 ring-offset-2 ring-blue-500'
                          : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowColorPicker(!showColorPicker)}
                  className="w-12 h-12 rounded-md border-2 border-gray-300"
                  style={{ backgroundColor: formData.backgroundColor }}
                  title="Custom color"
                />
                {showColorPicker && (
                  <div className="absolute right-0 mt-2 z-10">
                    <div className="absolute right-0 top-0">
                      <SketchPicker
                        color={formData.backgroundColor}
                        onChange={handleColorChange}
                        disableAlpha
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
            <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium"
                 style={{
                   backgroundColor: `${formData.backgroundColor}20`,
                   color: formData.textColor || calculateTextColor(formData.backgroundColor),
                   border: `1px solid ${formData.backgroundColor}40`
                 }}>
              <FlagIcon iconName={formData.iconName} className="h-4 w-4 mr-1" />
              <span>{formData.flagValue || 'Flag Text'}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FlagTemplateEditor;



